"use client";

import { useEffect } from "react";
import * as s from "@/lib/styles";

interface Props {
  open: boolean;
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** In-app confirmation dialog (replaces native confirm()). */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onCancel();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  return (
    <div
      className={s.modalOverlay + " z-50 p-4"}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className={s.modalContent}>
        <h2 id="confirm-title" className="text-base font-semibold text-foreground m-0">
          {title}
        </h2>
        {message != null && (
          <div className="text-sm text-muted break-words">{message}</div>
        )}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            className={s.btnAuxiliary}
            disabled={busy}
            onClick={onCancel}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={destructive ? s.btnDanger : s.btnPrimary}
            disabled={busy}
            onClick={onConfirm}
            autoFocus
          >
            {busy ? "…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
